import moment from "moment";
import UserService from "./UserService";
import UtilityService from "./UtilityService";
import { authedFetch, readJsonAnswer } from "./apiFetch";
import PlaybookResultsService from "./PlaybookResultsService";
import PlaybookReviewService from "./PlaybookReviewService";
import { postToAgents } from "./PlaybookApi";

const FINISHED_STATUSES = ["completed", "needs_review", "failed", "cancelled", "interrupted", "skipped", "needs_linking"];
const RESUMABLE_STATUSES = ["interrupted", "needs_linking"];
const STATUS_DESCRIPTIONS = {
	queued: { label: "Queued", tone: "neutral" },
	running: { label: "Running", tone: "accent" },
	completed: { label: "No action needed", tone: "success" },
	needs_review: { label: "Needs review", tone: "warning" },
	failed: { label: "Failed", tone: "danger" },
	cancelled: { label: "Cancelled", tone: "neutral" },
	interrupted: { label: "Interrupted", tone: "warning" },
	skipped: { label: "Skipped", tone: "neutral" },
	needs_linking: { label: "Needs linking", tone: "warning" },
};
const RUN_OUTPUT_KINDS = {
	excel: { label: "Excel workbook", look: "excel", opensInGoogleSlides: false },
	presentation_pptx: { label: "PowerPoint presentation", look: "powerpoint", opensInGoogleSlides: true },
	presentation_pdf: { label: "PDF presentation", look: "pdf", opensInGoogleSlides: false },
	letter_pdf: { label: "PDF letter", look: "pdf", opensInGoogleSlides: false },
};

const PlaybookRunService = {
	/**
	 * Starts an agent on one or more clients. Resolves to { runId, willRun, skipped }.
	 *
	 * @param {Object} params
	 * @param {Number} params.playbookId
	 * @param {Array<Number>} params.clientIds
	 * @param {Object} params.inputs
	 * @return {Promise<Object>}
	 */
	startRuns({ playbookId, clientIds, inputs }) {
		return postToAgents({ operationId: "startPlaybookRuns", body: { playbookId, clientIds, inputs } });
	},

	/**
	 * Each of an agent's sources and whether a link to it exists for this client and the viewer
	 * (isConnected: a token, a bank link, an inbox or a Google login is on record — not proof it
	 * still works). For a draft being tested, pass its draftVersionId.
	 *
	 * @param {Object} params { playbookId, clientId, draftVersionId }
	 * @return {Promise<Object>} { sources, canRun }
	 */
	checkRunSources({ playbookId, clientId, draftVersionId }) {
		return postToAgents({ operationId: "checkPlaybookRunSources", body: { playbookId, clientId, draftVersionId } });
	},

	/**
	 * The firm's active clients a playbook can run on now, every required source linked.
	 *
	 * @param {Object} params { playbookId }
	 * @return {Promise<Object>} { clients: [{ id, name }], totalClients, requiredSourceLabels }
	 */
	listConnectedClients({ playbookId }) {
		return postToAgents({ operationId: "listPlaybookConnectedClients", body: { playbookId } });
	},

	/**
	 * checkRunSources for several playbooks on one client in one request.
	 *
	 * @param {Object} params { playbookIds, clientId }
	 * @return {Promise<Object>} { byPlaybookId: { [playbookId]: { sources, canRun } } }
	 */
	checkRunSourcesForPlaybooks({ playbookIds, clientId }) {
		return postToAgents({ operationId: "checkPlaybookRunSources", body: { playbookIds, clientId } });
	},

	/**
	 * What a run of an agent must be started with: a written request, a period, or both. The
	 * agents list, the editing view and a build draft all carry these on the agent itself, as
	 * true or false (takesRequest, needsPeriod).
	 *
	 * @param {Object} params
	 * @param {Object} params.agent an agent from listPlaybooks, getPlaybookForEditing, or a build draft
	 * @return {Object} { takesRequest, needsPeriod }
	 */
	runInputsNeededBy({ agent }) {
		const needed = { takesRequest: agent.takesRequest, needsPeriod: agent.needsPeriod };
		return needed;
	},

	/**
	 * The inputs a run is started with: the period when the agent reviews one, the person's
	 * request when it takes one, and the run form's values (fieldValues, from
	 * PlaybookInputFieldService.valuesForRun) when the version declares input fields. Nothing is
	 * sent for what the agent does not use, so a version without fields starts exactly as before.
	 *
	 * @param {Object} params
	 * @param {Boolean} params.needsPeriod
	 * @param {String} [params.periodEnd] YYYY-MM-DD
	 * @param {Boolean} params.takesRequest
	 * @param {String} [params.request]
	 * @param {Object} [params.fieldValues] { <key>: <value> }, omitted when the version has no fields
	 * @return {Object}
	 */
	inputsForRun({ needsPeriod, periodEnd, takesRequest, request, fieldValues }) {
		const periodInputs = needsPeriod ? this.inputsForPeriod({ periodEnd }) : {};
		const requestText = String(request || "").trim();
		const requestInputs = takesRequest ? { request: requestText } : {};
		const fieldInputs = fieldValues ? { fieldValues } : {};
		const inputs = { ...periodInputs, ...requestInputs, ...fieldInputs };
		return inputs;
	},

	/**
	 * Whether a run can be started with what the person has filled in: a request is written
	 * when the agent takes one.
	 *
	 * @param {Object} params
	 * @param {Boolean} params.takesRequest
	 * @param {String} params.request
	 * @return {Boolean}
	 */
	isRunRequestReady({ takesRequest, request }) {
		const hasRequest = String(request || "").trim().length > 0;
		const isReady = !takesRequest || hasRequest;
		return isReady;
	},

	/**
	 * The request a run was started with, or "" when it took none.
	 *
	 * @param {Object} params
	 * @param {Object} params.run
	 * @return {String}
	 */
	requestOfRun({ run }) {
		const inputs = run && run.inputs ? run.inputs : {};
		const request = String(inputs.request || "").trim();
		return request;
	},

	/**
	 * The files a run made — workbooks and presentations — newest last.
	 *
	 * @param {Object} params
	 * @param {Number} params.runId
	 * @return {Promise<Array>} [{ id, kind, title, fileName, size, createdAt }]
	 */
	listRunOutputs({ runId }) {
		return postToAgents({ operationId: "listPlaybookRunOutputs", body: { runId } });
	},

	/**
	 * Downloads one file a run made and hands it to the browser under its file name. The file
	 * comes back as bytes, so this goes through authedFetch rather than Kichiri. Throws with the
	 * server's reason when it refuses.
	 *
	 * @param {Object} params
	 * @param {Object} params.output { id, fileName }
	 * @return {Promise<void>}
	 */
	async downloadRunOutput({ output }) {
		const requestBody = JSON.stringify({ outputId: output.id });
		const response = await authedFetch("/playbooks/runs/outputs/download", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: requestBody,
		});
		if (!response.ok) {
			const answer = await readJsonAnswer({ response });
			throw new Error(answer.error || "The file could not be downloaded.");
		}
		const blob = await response.blob();
		UtilityService.downloadFile({ blob, fileName: output.fileName });
	},

	/**
	 * What kind of file a run output is, in words, with the look its row takes and whether it opens
	 * in Google Slides once uploaded to Drive (a .pptx does; a PDF deck does not).
	 *
	 * @param {Object} params
	 * @param {String} params.kind excel | presentation_pptx | presentation_pdf | letter_pdf
	 * @return {Object} { label, look, opensInGoogleSlides }
	 */
	describeRunOutputKind({ kind }) {
		const description = RUN_OUTPUT_KINDS[kind] || { label: "File", look: "file", opensInGoogleSlides: false };
		return description;
	},

	/**
	 * What the Connect button on a missing source says and where it goes, for this viewer. Team
	 * Gmail inboxes are connected by administrators, so anyone else is told to ask one.
	 *
	 * @param {Object} params
	 * @param {String} params.connectWhere client_connections | inbox_settings | my_google
	 * @return {Object} { label, path, isOnClientPage, canConnect }
	 */
	describeConnectAction({ connectWhere }) {
		if (connectWhere === "client_connections") return { label: "Open Connections", path: null, isOnClientPage: true, canConnect: true };
		if (connectWhere === "inbox_settings")
			return { label: "Open inbox settings", path: "/settings/inbox", isOnClientPage: false, canConnect: UserService.isAdministrator() };
		if (connectWhere === "my_google")
			return { label: "Connect Google", path: `/settings/users/${UserService.getUserData().userId}`, isOnClientPage: false, canConnect: true };
		return { label: "", path: null, isOnClientPage: false, canConnect: false };
	},

	/**
	 * A period end shown as its month, e.g. "Sep 2026" or, long, "September 2026"; empty when the
	 * run has no period. Every place that names a run's period, from the choices offered to the
	 * inputs a run starts with, writes it through here.
	 *
	 * @param {Object} params
	 * @param {String} params.periodEnd YYYY-MM-DD
	 * @param {Boolean} [params.isLong]
	 * @return {String}
	 */
	periodLabelFor({ periodEnd, isLong = false }) {
		const label = UtilityService.formatDate({ value: periodEnd, format: isLong ? "MMMM YYYY" : "MMM YYYY", fallback: "" });
		return label;
	},

	/**
	 * A person's name shortened for a crowded row: first name and last initial.
	 *
	 * @param {Object} params
	 * @param {Object} params.user { first_name, last_name }
	 * @return {String}
	 */
	shortNameFor({ user }) {
		if (!user) return "";
		const lastInitial = user.last_name ? ` ${user.last_name.charAt(0)}.` : "";
		return `${user.first_name}${lastInitial}`;
	},

	/**
	 * One run with its findings, checks, proposals and any per-client child runs.
	 *
	 * @param {Object} params
	 * @param {Number} params.runId
	 * @return {Promise<Object>}
	 */
	getRun({ runId }) {
		return postToAgents({ operationId: "getPlaybookRun", body: { runId } });
	},

	/**
	 * A run's steps after the given step number.
	 *
	 * @param {Object} params
	 * @param {Number} params.runId
	 * @param {Number} params.afterSequence
	 * @return {Promise<Array>}
	 */
	listSteps({ runId, afterSequence }) {
		return postToAgents({ operationId: "listPlaybookRunSteps", body: { runId, afterSequence } });
	},

	/**
	 * Cancels a run that has not finished.
	 *
	 * @param {Object} params
	 * @param {Number} params.runId
	 * @return {Promise<Object>} { cancelledCount }
	 */
	cancelRun({ runId }) {
		return postToAgents({ operationId: "cancelPlaybookRun", body: { runId } });
	},

	/**
	 * Resumes a run that stopped part-way, or a multi-client run's clients that stopped part-way.
	 * Every step runs again; what the earlier attempt did stays on the run, set aside.
	 *
	 * @param {Object} params
	 * @param {Number} params.runId
	 * @return {Promise<Object>} { resumedCount, notResumed: [String] }
	 */
	resumeRun({ runId }) {
		return postToAgents({ operationId: "resumePlaybookRun", body: { runId } });
	},

	/**
	 * Whether a run offers Resume: it stopped part-way or is waiting for accounts to be linked, or it
	 * covers several clients and one of them is.
	 *
	 * @param {Object} params
	 * @param {Object} params.run
	 * @return {Boolean}
	 */
	canResume({ run }) {
		const isStopped = RESUMABLE_STATUSES.includes(run.status);
		const hasStoppedClient = (run.children || []).some(child => RESUMABLE_STATUSES.includes(child.status));
		const isResumable = isStopped || hasStoppedClient;
		return isResumable;
	},

	/**
	 * The twelve most recent fully completed months, newest first, as period choices whose value
	 * is the month's last day.
	 *
	 * @return {Array} [{ value, label }]
	 */
	recentCompletedMonths() {
		const months = [];
		for (let monthsBack = 1; monthsBack <= 12; monthsBack++) {
			const month = moment().subtract(monthsBack, "months");
			const periodEnd = month.endOf("month").format("YYYY-MM-DD");
			const label = this.periodLabelFor({ periodEnd, isLong: true });
			months.push({ value: periodEnd, label });
		}
		return months;
	},

	/**
	 * The inputs a month-end run is started with, from a chosen period end.
	 *
	 * @param {Object} params
	 * @param {String} params.periodEnd YYYY-MM-DD
	 * @return {Object}
	 */
	inputsForPeriod({ periodEnd }) {
		const periodStart = moment(periodEnd).startOf("month").format("YYYY-MM-DD");
		const periodLabel = this.periodLabelFor({ periodEnd, isLong: true });
		const inputs = { period: `${periodLabel} (${periodStart} to ${periodEnd})`, periodLabel, periodEnd };
		return inputs;
	},

	/**
	 * The names a run is shown with: its agent, its client (or that it covers several), the
	 * period it reviewed and who started it. Runs started before the period end was stored
	 * show no period rather than one guessed from the instructions text. When tools the playbook
	 * uses changed after its version was published, the run still went ahead, and the note says
	 * so: which tools, from which version to which, and that the playbook should be tested and
	 * republished. The note is null when nothing changed. costLabel is what the run cost in US
	 * dollars ("so far" while it runs), "cost not known" for a finished run whose model has no
	 * price, and null for a running run not yet priced.
	 *
	 * @param {Object} params
	 * @param {Object} params.run
	 * @return {Object} { agentName, clientName, periodLabel, startedBy, toolChangeNote: { label, detail } | null, costLabel }
	 */
	describeRun({ run }) {
		const agentName = run.agent_version ? run.agent_version.name : "";
		const clientName = run.client ? run.client.name : "Several clients";
		const periodLabel = this.periodLabelFor({ periodEnd: run.period_end, isLong: true });
		const startedBy = run.started_by_user ? `${run.started_by_user.first_name} ${run.started_by_user.last_name}` : "";
		const toolChanges = run.toolChanges || [];
		const changedToolNames = toolChanges.map(change => `${change.tool_label} (v${change.published_version} → v${change.current_version})`);
		const toolChangeDetail = `${changedToolNames.join(", ")} changed after this playbook was published. The run went ahead with today's tools; test the playbook and republish it.`;
		const toolChangeNote = toolChanges.length > 0 ? { label: "Changed since publish", detail: toolChangeDetail } : null;
		const isFinished = this.isFinished({ status: run.status });
		const isCostKnown = !UtilityService.isNullish(run.cost_usd);
		const costInDollars = `US$${UtilityService.formatCurrency(Number(run.cost_usd))}`;
		let costLabel = null;
		if (isCostKnown) costLabel = isFinished ? `cost ${costInDollars}` : `cost ${costInDollars} so far`;
		else if (isFinished) costLabel = "cost not known";
		const description = { agentName, clientName, periodLabel, startedBy, toolChangeNote, costLabel };
		return description;
	},

	/**
	 * Whether a run has stopped, for whatever reason.
	 *
	 * @param {Object} params
	 * @param {String} params.status
	 * @return {Boolean}
	 */
	isFinished({ status }) {
		const finished = FINISHED_STATUSES.includes(status);
		return finished;
	},

	/**
	 * The plain label and colour tone for a run status.
	 *
	 * @param {Object} params
	 * @param {String} params.status
	 * @return {Object} { label, tone }
	 */
	describeStatus({ status }) {
		const description = STATUS_DESCRIPTIONS[status] || { label: status, tone: "neutral" };
		return description;
	},

	/**
	 * A run's findings by severity, as the server counted them when the run finished.
	 *
	 * @param {Object} params
	 * @param {Object} params.run
	 * @return {Object} { high, medium, low }
	 */
	countFindings({ run }) {
		const counts = { high: run.high_count || 0, medium: run.medium_count || 0, low: run.low_count || 0 };
		return counts;
	},

	/**
	 * The label and tone for where a run ended up: its decision stage when it is in one, else its
	 * status. A run needing review with no high or medium findings says, from its own counts, why
	 * it needs a person: checks it could not do, only low findings, both, or — with neither on
	 * record — that it is worth a look, the name of the results group it sits in.
	 *
	 * @param {Object} params
	 * @param {Object} params.run
	 * @return {Object} { label, tone }
	 */
	describeRunOutcome({ run }) {
		const stage = PlaybookReviewService.describeStage({ stage: run.review_stage });
		if (stage) return stage;
		const isNeedsReview = run.status === "needs_review";
		const seriousCount = (Number(run.high_count) || 0) + (Number(run.medium_count) || 0);
		if (!isNeedsReview || seriousCount > 0) return this.describeStatus({ status: run.status });

		const lowCount = Number(run.low_count) || 0;
		const notCheckedCount = Number(run.not_checked_count) || 0;
		if (lowCount > 0 && notCheckedCount > 0) return { label: `Low findings · couldn't check ${notCheckedCount}`, tone: "warning" };
		if (lowCount > 0) return { label: "Low findings only", tone: "warning" };
		if (notCheckedCount > 0) return { label: "Couldn't check everything", tone: "warning" };
		const worthALook = PlaybookResultsService.resultSegments().find(segment => segment.key === "worth_a_look");
		const outcome = { label: worthALook.label, tone: worthALook.tone };
		return outcome;
	},

	/**
	 * Why a run produced no findings, in its first sentence only, for lists: its skip reason, or
	 * the error it stopped on (the report shows the whole of it). Null for a run that ran.
	 *
	 * @param {Object} params
	 * @param {Object} params.run
	 * @return {String|null}
	 */
	reasonRunDidNotRun({ run }) {
		const isStopped = run.status === "failed" || RESUMABLE_STATUSES.includes(run.status);
		const reason = (run.status === "skipped" && run.skip_reason) || (isStopped && run.error) || null;
		if (!reason) return null;
		const endOfFirstSentence = reason.search(/[.!?](\s|$)/);
		const firstSentence = endOfFirstSentence === -1 ? reason : reason.slice(0, endOfFirstSentence + 1);
		return firstSentence;
	},

	/**
	 * The checks that passed, from a run's stored check results.
	 *
	 * @param {Object} params
	 * @param {Array} params.checks
	 * @return {Array}
	 */
	passedChecks({ checks }) {
		const passes = (checks || []).filter(check => check.status === "pass");
		return passes;
	},
};

export default PlaybookRunService;
